// src/pages/Coleta/ColetaDetalhePage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    GlobalStyles,
    Grid,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import ComentariosAnaliseDialog from '../../components/ComentariosAnaliseDialog';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import ResultadoLabDialog from '../../components/ResultadoLabDialog';
import { SnackbarAlert } from '../../components/SnackbarAlert';
import { useBootstrap } from '../../context/BootstrapContext';
import useExperimentosAtivos from '../../hooks/useExperimentosAtivos';
import { usePermissao } from '../../security/usePermissao';
import { adicionarComentarioAmostra } from '../../infra/api/Amostras';
import {
    atualizarDiluicaoExperimento,
    encerrarExperimento,
    processarExperimento,
    restaurarExperimento,
} from '../../infra/api/ExperimentoApi';
import { getLabResultByExperimentId } from '../../infra/api/labResultApi';
import {
    buscarColeta,
    cancelarColeta,
    excluirColeta,
    listarPaginasColeta,
    listarPontosColeta,
    mensagemErro,
    origensColeta,
} from '../../infra/api/Coletas';
import {
    PAGINAS_COLETA,
    dataDoExperimento,
    formatarDataHora,
    rotuloAlvo,
    situacaoColeta,
    unidadeExibida,
} from '../../utils/coleta';
import {
    compararComModelo,
    entradaLabDaPagina,
    entradasLabDoProtocolo,
    normalizarResultadoLab,
} from '../../utils/resultadoLab';
import ColetaDialog from './components/ColetaDialog';
import {
    InformacoesCard,
    LaboratorioCard,
    LinhaDoTempo,
    ObservacoesCard,
    ResultadoCard,
} from './detalhe/SecoesDetalhe';
import { DiluicaoDialog, ExcluirColetaDialog } from './detalhe/DialogosDetalhe';
import { MARGEM_LATERAL } from '../../theme/escalaPagina';

const MINUTO = 60_000;

/**
 * Impressão = o relatório da amostra: some a navegação do app e os botões
 * (`data-nao-imprimir`), os cartões não quebram no meio da página.
 */
const ESTILO_IMPRESSAO = {
    '@media print': {
        '.MuiAppBar-root, .MuiDrawer-root, [data-nao-imprimir]': { display: 'none !important' },
        'main, main > .MuiContainer-root': { margin: '0 !important', padding: '0 !important', width: '100% !important', maxWidth: '100% !important' },
        body: { background: '#fff !important' },
    },
};

/**
 * Detalhe de uma amostra das páginas de coleta (`/leite/:amostraId`…): resultado do
 * modelo, laboratório com a comparação, dados da coleta, observações e linha do tempo.
 */
export default function ColetaDetalhePage({ pagina }) {
    const meta = PAGINAS_COLETA[pagina];
    const { tenant, amostraId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { bootstrap } = useBootstrap();
    const { pode } = usePermissao();
    const podeAlterar = pode('dados.alterar');
    const podeProcessar = podeAlterar && pode('amostra.processarIA');
    const podeLancarLab = podeAlterar && pode('amostra.resultadoLab');
    const podeVerLaboratorio = pode('coleta.compararLaboratorio');
    const podeDiluir = meta.usaDiluicao && podeAlterar && pode('amostra.diluicao');
    const podeExcluir = pode('coleta.excluir');
    const podeRestaurar = pode('lixeira.restaurar');

    const voltar = `/${tenant}/${meta.rota}`;

    const [detalhe, setDetalhe] = useState(null);
    const [config, setConfig] = useState(null);
    const [erroCarga, setErroCarga] = useState('');
    const [labResult, setLabResult] = useState(null);
    const [carregandoLab, setCarregandoLab] = useState(false);
    const [pontos, setPontos] = useState([]);
    const [origens, setOrigens] = useState([]);
    const [agora, setAgora] = useState(() => new Date());
    const [processando, setProcessando] = useState(false);
    const [restaurando, setRestaurando] = useState(false);

    const [menu, setMenu] = useState(null);
    const [dialogo, setDialogo] = useState(null); // 'editar' | 'lab' | 'diluicao' | 'encerrar' | 'excluir' | 'observacoes'
    const [aviso, setAviso] = useState({ open: false, message: '', severity: 'info' });
    const avisar = (severity, message) => setAviso({ open: true, severity, message });

    const carregar = useCallback(async () => {
        try {
            const [det, paginas] = await Promise.all([buscarColeta(amostraId), listarPaginasColeta()]);
            setDetalhe(det);
            setConfig((paginas || []).find((c) => c.pagina === det.pagina) || null);
            setErroCarga('');
        } catch (e) {
            setErroCarga(
                e?.response?.status === 404
                    ? 'Amostra não encontrada. Ela pode ter sido excluída.'
                    : mensagemErro(e, 'Não foi possível carregar a amostra.')
            );
        }
    }, [amostraId]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const resumo = detalhe?.resumo;
    const experimentoId = resumo?.experimento?.id;

    // Laboratório: do experimento ligado; não existe antes do tubo entrar.
    useEffect(() => {
        if (!experimentoId) {
            setLabResult(null);
            return undefined;
        }
        let ativo = true;
        setCarregandoLab(true);
        getLabResultByExperimentId(experimentoId)
            .then((r) => ativo && setLabResult(normalizarResultadoLab(r)))
            .catch(() => ativo && setLabResult(null))
            .finally(() => ativo && setCarregandoLab(false));
        return () => {
            ativo = false;
        };
    }, [experimentoId]);

    // Pontos e origens só servem ao "Editar dados da coleta".
    useEffect(() => {
        if (dialogo !== 'editar' || !config) return;
        listarPontosColeta({ analiseId: config.analiseId }).then(setPontos).catch(() => setPontos([]));
        origensColeta(config.pagina).then(setOrigens).catch(() => setOrigens([]));
    }, [dialogo, config]);

    // ── ao vivo enquanto aguarda o tubo ou o ensaio roda ──
    const aoVivo = resumo?.status === 'PENDENTE' || resumo?.status === 'EM_PROCESSAMENTO';
    const { experimentoPorSerial } = useExperimentosAtivos({ enabled: aoVivo });
    const carregarRef = useRef(carregar);
    carregarRef.current = carregar;
    const mapaAnteriorRef = useRef(null);
    useEffect(() => {
        const mudou = mapaAnteriorRef.current !== null && mapaAnteriorRef.current !== experimentoPorSerial;
        mapaAnteriorRef.current = experimentoPorSerial;
        if (!mudou || !aoVivo) return undefined;
        const t = setTimeout(() => carregarRef.current(), 2000);
        return () => clearTimeout(t);
    }, [experimentoPorSerial, aoVivo]);
    useEffect(() => {
        if (!aoVivo) return undefined;
        const id = setInterval(() => {
            setAgora(new Date());
            carregarRef.current();
        }, MINUTO);
        return () => clearInterval(id);
    }, [aoVivo]);

    // ── derivados ──
    const protocolo = useMemo(
        () => (bootstrap?.protocolos || []).find((p) => p.id === config?.protocoloId),
        [bootstrap?.protocolos, config?.protocoloId]
    );
    const unidade = unidadeExibida(protocolo, config?.unidade);
    const entradasLab = useMemo(() => entradasLabDoProtocolo(protocolo), [protocolo]);
    const unidadeComparavel = useCallback(
        (unit) => String(unit || '').toUpperCase().startsWith(String(config?.unidade || '').toUpperCase()),
        [config?.unidade]
    );
    const entradaPagina = entradaLabDaPagina(labResult, { bacteriaId: config?.bacteriaId, unidade: config?.unidade });
    const log10Modelo = resumo?.experimento?.log10Contagem;
    const comparacao = compararComModelo(log10Modelo, entradaPagina?.nmp);

    const etapas = useMemo(() => {
        if (!resumo) return [];
        const exp = resumo.experimento;
        return [
            { rotulo: 'Amostra registrada', data: resumo.registradoEm ? new Date(resumo.registradoEm) : null },
            { rotulo: 'Coleta', data: resumo.coleta?.dataHora ? new Date(resumo.coleta.dataHora) : null },
            { rotulo: 'Análise iniciada', data: dataDoExperimento(exp?.inicio) },
            { rotulo: 'Ensaio finalizado', data: dataDoExperimento(exp?.fim) },
            { rotulo: 'Resultado disponível', data: detalhe?.processamento?.processadoEm ? new Date(detalhe.processamento.processadoEm) : null },
        ];
    }, [resumo, detalhe?.processamento]);

    // A URL de outra página de coleta (ex.: /leite/<id de uma amostra de água>) vai para a certa.
    if (detalhe && detalhe.pagina !== pagina) {
        return <Navigate to={`/${tenant}/${PAGINAS_COLETA[detalhe.pagina].rota}/${amostraId}`} replace />;
    }

    // ── ações ──
    const restaurar = async () => {
        setRestaurando(true);
        try {
            await restaurarExperimento(experimentoId);
            await carregar();
            avisar('success', 'Ensaio restaurado.');
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível restaurar o ensaio.'));
        } finally {
            setRestaurando(false);
        }
    };

    const processar = async () => {
        setProcessando(true);
        try {
            await processarExperimento(experimentoId);
            await carregar();
            avisar('success', 'Amostra processada.');
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível processar a amostra.'));
        } finally {
            setProcessando(false);
        }
    };

    const encerrar = async () => {
        setDialogo(null);
        try {
            await encerrarExperimento(experimentoId);
            await carregar();
            avisar('success', 'Ensaio encerrado.');
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível encerrar o ensaio.'));
        }
    };

    const salvarDiluicao = async (valor) => {
        await atualizarDiluicaoExperimento(experimentoId, valor);
        setDialogo(null);
        await carregar();
        avisar('success', 'Diluição atualizada.');
    };

    const excluir = async (motivo) => {
        await excluirColeta(amostraId, motivo);
        navigate(voltar, { replace: true });
    };

    const cancelar = async () => {
        setDialogo(null);
        try {
            await cancelarColeta(amostraId);
            navigate(voltar, { replace: true });
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível cancelar o registro.'));
            carregar();
        }
    };

    const adicionarObservacao = async (texto) => {
        const salva = await adicionarComentarioAmostra(amostraId, texto);
        setDetalhe((d) => ({ ...d, comentarios: salva.comentarios || [] }));
    };

    const verGraficos = () =>
        navigate(`/${tenant}/graficos/${experimentoId}`, { state: { voltarPara: location.pathname } });

    const ativo = String(resumo?.experimento?.status).toLowerCase() === 'active';
    // Com o ensaio excluído, nada se faz nele até ser restaurado.
    const ensaioUtil = Boolean(experimentoId) && !resumo?.experimento?.excluido;
    const acoesMenu = resumo
        ? [
              podeAlterar && { chave: 'editar', rotulo: 'Editar dados da coleta', icone: <Lucide.Pencil size={18} /> },
              // A acao nasce com esta amostra como o "antes" da intervencao.
              pode('planoAcao.gerenciar') && resumo.experimento?.processado && { chave: 'planoAcao', rotulo: 'Abrir plano de ação', icone: <Lucide.ClipboardList size={18} /> },
              ensaioUtil && podeLancarLab && { chave: 'lab', rotulo: labResult ? 'Atualizar resultado de laboratório' : 'Adicionar resultado de laboratório', icone: <Lucide.Microscope size={18} /> },
              ensaioUtil && podeDiluir && { chave: 'diluicao', rotulo: 'Ajustar diluição', icone: <Lucide.Droplet size={18} /> },
              ensaioUtil && ativo && podeAlterar && { chave: 'encerrar', rotulo: 'Encerrar ensaio', icone: <Lucide.CircleStop size={18} /> },
              podeAlterar && resumo.status === 'PENDENTE' && { chave: 'cancelar', rotulo: 'Cancelar registro', icone: <Lucide.Ban size={18} />, perigo: true },
              podeExcluir && !ativo && resumo.status !== 'PENDENTE' && { chave: 'excluir', rotulo: 'Excluir amostra', icone: <Lucide.Trash2 size={18} />, perigo: true },
          ].filter(Boolean)
        : [];

    // ── telas de carga e erro ──
    if (erroCarga) {
        return (
            <Box sx={{ px: MARGEM_LATERAL, pt: 3 }}>
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
                    <Typography fontWeight={700} sx={{ mb: 1 }}>{erroCarga}</Typography>
                    <Button variant="contained" disableElevation onClick={() => navigate(voltar)} sx={{ borderRadius: 2 }}>
                        Voltar ao histórico
                    </Button>
                </Paper>
            </Box>
        );
    }
    if (!detalhe) {
        return (
            <Stack spacing={2} sx={{ px: MARGEM_LATERAL, pt: 3 }}>
                <Skeleton variant="text" width={220} height={48} />
                <Skeleton variant="rounded" height={200} />
                <Skeleton variant="rounded" height={260} />
            </Stack>
        );
    }

    const situacao = situacaoColeta(resumo.status);
    const subtitulo = [detalhe.analiseNome, detalhe.pontoColetaNome, resumo.coleta?.origem].filter(Boolean).join(' · ');
    const dispositivo = detalhe.dispositivoNome || resumo.numeroSerie;

    return (
        <Box sx={{ px: MARGEM_LATERAL, pt: { xs: 2, sm: 2.5 }, pb: { xs: 3, md: 4 } }}>
            <GlobalStyles styles={ESTILO_IMPRESSAO} />

            {/* Só na impressão: identifica o relatório. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'none', '@media print': { display: 'block', mb: 1 } }}>
                Relatório da amostra · BioAILab · gerado em {formatarDataHora(new Date())}
            </Typography>

            <Button
                startIcon={<Lucide.ChevronLeft size={18} />}
                onClick={() => navigate(voltar)}
                color="inherit"
                sx={{ mb: 1.5, color: 'text.secondary', px: 0.5 }}
                data-nao-imprimir
            >
                Voltar ao histórico
            </Button>

            {/* Cabeçalho */}
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2, fontWeight: 700 }}>
                        Controle microbiológico
                    </Typography>
                    <Typography component="h1" fontWeight={800} sx={{ fontSize: { xs: '1.75rem', sm: '2rem', xl: '2.25rem' }, lineHeight: 1.15, wordBreak: 'break-word' }}>
                        {resumo.identificacao}
                    </Typography>
                    {subtitulo && <Typography color="text.secondary" sx={{ mt: 0.5 }}>{subtitulo}</Typography>}
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                        <Chip size="small" variant="outlined" color={situacao.cor} label={situacao.rotulo} sx={{ fontWeight: 600 }} />
                        {podeVerLaboratorio && labResult && <Chip size="small" variant="outlined" icon={<Lucide.Microscope size={14} />} label="Laboratório lançado" />}
                    </Stack>
                </Box>

                <Stack spacing={1} alignItems={{ xs: 'stretch', md: 'flex-end' }} data-nao-imprimir>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<Lucide.ChartNoAxesCombined size={18} />}
                            onClick={verGraficos}
                            disabled={!ensaioUtil}
                            sx={{ borderRadius: 2, flex: { xs: 1, md: 'none' } }}
                        >
                            Ver gráficos
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Lucide.Printer size={18} />}
                            onClick={() => window.print()}
                            sx={{ borderRadius: 2, flex: { xs: 1, md: 'none' } }}
                        >
                            Relatório
                        </Button>
                        {acoesMenu.length > 0 && (
                            <IconButton onClick={(e) => setMenu(e.currentTarget)} aria-label="Mais ações" sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
                                <Lucide.MoreVertical size={18} />
                            </IconButton>
                        )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: { md: 'right' } }}>
                        {ensaioUtil
                            ? 'Relatório pronto para imprimir ou salvar em PDF'
                            : resumo.experimento?.excluido
                              ? 'Gráficos indisponíveis: o ensaio foi excluído'
                              : 'Gráficos disponíveis depois que o tubo entrar'}
                    </Typography>
                </Stack>
            </Stack>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <ResultadoCard
                        resumo={resumo}
                        processamento={detalhe.processamento}
                        unidade={unidade}
                        rotuloMedida={rotuloAlvo(config?.alvo)}
                        dispositivo={dispositivo}
                        agora={agora}
                        processando={processando}
                        podeProcessar={podeProcessar}
                        onProcessar={processar}
                        podeRestaurar={podeRestaurar}
                        restaurando={restaurando}
                        onRestaurar={restaurar}
                        mostrarDiluicao={meta.usaDiluicao}
                        alvosQualitativos={meta.qualitativo ? meta.alvosQualitativos : null}
                    />
                </Grid>
                {podeVerLaboratorio && (
                    <Grid item xs={12}>
                        <LaboratorioCard
                            temExperimento={ensaioUtil}
                            carregando={carregandoLab}
                            labResult={labResult}
                            entradaPagina={entradaPagina}
                            comparacao={comparacao}
                            log10Modelo={log10Modelo}
                            unidade={unidade}
                            podeLancar={podeLancarLab}
                            onLancar={() => setDialogo('lab')}
                        />
                    </Grid>
                )}
                <Grid item xs={12} md={7}>
                    <Stack spacing={3}>
                        <InformacoesCard resumo={resumo} dispositivoNome={detalhe.dispositivoNome} agora={agora} mostrarDiluicao={meta.usaDiluicao} />
                        <ObservacoesCard comentarios={detalhe.comentarios} podeComentar={podeAlterar} onAbrir={() => setDialogo('observacoes')} />
                    </Stack>
                </Grid>
                <Grid item xs={12} md={5}>
                    <LinhaDoTempo etapas={etapas} />
                </Grid>
            </Grid>

            <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
                {acoesMenu.map((a) => (
                    <MenuItem
                        key={a.chave}
                        onClick={() => {
                            setMenu(null);
                            if (a.chave === 'planoAcao') navigate(`/${tenant}/acoes?novo=${resumo.id}`);
                            else setDialogo(a.chave);
                        }}
                        sx={a.perigo ? { color: 'error.main' } : undefined}
                    >
                        <ListItemIcon sx={a.perigo ? { color: 'error.main' } : undefined}>{a.icone}</ListItemIcon>
                        {a.rotulo}
                    </MenuItem>
                ))}
            </Menu>

            {config && (
                <ColetaDialog
                    open={dialogo === 'editar'}
                    modo="editar"
                    pagina={pagina}
                    config={config}
                    unidade={unidade}
                    pontos={pontos}
                    origens={origens}
                    linha={resumo}
                    onClose={() => setDialogo(null)}
                    onSalvo={() => {
                        setDialogo(null);
                        carregar();
                        avisar('success', 'Dados da coleta salvos.');
                    }}
                />
            )}

            {experimentoId && (
                <ResultadoLabDialog
                    open={dialogo === 'lab'}
                    onClose={() => setDialogo(null)}
                    experimentId={experimentoId}
                    entradas={entradasLab}
                    labResult={labResult}
                    unidadeComparavel={unidadeComparavel}
                    onSalvo={(salvo) => {
                        setLabResult(salvo);
                        setDialogo(null);
                        avisar('success', 'Resultado de laboratório salvo.');
                    }}
                />
            )}

            <DiluicaoDialog
                open={dialogo === 'diluicao'}
                valorAtual={resumo.experimento?.diluicao}
                onClose={() => setDialogo(null)}
                onSalvar={salvarDiluicao}
            />

            <ExcluirColetaDialog
                open={dialogo === 'excluir'}
                identificacao={resumo.identificacao}
                onClose={() => setDialogo(null)}
                onConfirmar={excluir}
            />

            <ConfirmationDialog
                open={dialogo === 'encerrar'}
                onClose={() => setDialogo(null)}
                onConfirm={encerrar}
                title="Encerrar ensaio"
                message={`Encerrar o ensaio de ${resumo.identificacao} em ${dispositivo}? Os dados coletados até agora ficam salvos.`}
                confirmText="Encerrar"
                cancelText="Voltar"
            />

            <ConfirmationDialog
                open={dialogo === 'cancelar'}
                onClose={() => setDialogo(null)}
                onConfirm={cancelar}
                title="Cancelar registro"
                message={`Cancelar o registro ${resumo.identificacao}? O dispositivo fica livre para outra análise.`}
                confirmText="Cancelar registro"
                cancelText="Voltar"
            />

            <ComentariosAnaliseDialog
                open={dialogo === 'observacoes'}
                onClose={() => setDialogo(null)}
                identificacao={resumo.identificacao}
                comentarios={detalhe.comentarios}
                onAdicionar={adicionarObservacao}
                somenteLeitura={!podeAlterar}
            />

            <SnackbarAlert open={aviso.open} onClose={() => setAviso((a) => ({ ...a, open: false }))} severity={aviso.severity} message={aviso.message} />
        </Box>
    );
}
